import ContractHistory from "../../../../components/enrollment/contractHistory";
import FirmaEstado from "../../../../components/enrollment/firmaEstado";
import React, { useEffect, useRef, useState } from "react";
import { Badge, Dropdown, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { formatLocalDateTime } from "../../../../components/enrollment/format";
import { EnrollmentRequestDetailsDTO, RequestContractDTO, RequestDocumentDTO, REQUEST_MODE, REQUEST_STATUS } from "../../../../models/EnrollmentHistoryDTO";
import { AdjuntosInput, AdjuntosVista, capturasDelPortapapeles } from "../../../../components/enrollment/adjuntos";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { horarioBonito } from "../../../../utils/horarioBonito";
import { telefonoBonito, telefonosBonitos } from "../../../../utils/telefonoBonito";
import useEsTelefono from "../../../../utils/esTelefono";
import S from "./strings";
import TutorContratoModal from "./tutorContratoModal";
import { errorText, formatDate, formatMoney, estadoVariant, HORARIO_LISTA } from "./helpers";
import { abrirArchivo } from "../../../../components/enrollment/visorArchivo";
import { RevisionIdentificacion } from "../../../../services/v2/enrollmentRequests.service";
import SchedulePicker from "../../../../components/enrollment/schedulePicker";
import { AvailableScheduleDTO } from "../../../../models/EnrollmentHistoryDTO";

export interface RequestDetailsModalProps {
	campusId: number;
	/** País del plantel: con él se muestra cada teléfono como lo escribe la gente de ahí. */
	countryId?: number;
	requestId: number;
	onClose: () => void;
	/** Called after any state change so the list refreshes. */
	onChanged: () => void;
	onEdit: (requestId: number) => void;
}

/*
	Tres ventanitas nada más (Esteban, 2026-09-23): Rechazar, Reasignar (que ya incluye escalar) y
	Enviar a Caja el comprobante nuevo. "Regresar a borrador" ya no existe.
*/
type PromptKind = "return" | "reassign" | "resend" | "error" | "horario" | null;

/** Segundos de arrepentimiento antes de que la matrícula se ejecute (Esteban, 2026-09-22). */
const SEGUNDOS_PARA_DESHACER = 10;

/**
 * Una línea de dato. En pantalla ancha va etiqueta a la izquierda y valor a la derecha;
 * en teléfono la etiqueta va arriba, porque 170px de etiqueta no caben.
 */
const Row: React.FC<{ label: string; children: React.ReactNode; movil: boolean }> = ({ label, children, movil }) => (
	<div style={{ display: "flex", flexDirection: movil ? "column" : "row", gap: movil ? 0 : 8, padding: movil ? "5px 0" : "2px 0", borderBottom: "1px dotted var(--wa-filo)" }}>
		<div style={{ width: movil ? "auto" : 170, minWidth: movil ? 0 : 170, color: "var(--wa-apagado)", fontSize: movil ? 13 : undefined, textTransform: movil ? "uppercase" : undefined }}>{label}</div>
		<div style={{ flex: 1 }}>{children}</div>
	</div>
);

/** Read-only view of a request with the workflow actions the current role is allowed to take. */
const RequestDetailsModal: React.FC<RequestDetailsModalProps> = ({ campusId, countryId, requestId, onClose, onChanged, onEdit }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const basicInfo: any = useSelector((state: RootState) => state.basicInfo.value);
	//Catálogos para mostrar nombres y no ids: grado de estudios y tipo de identificación (2026-09-24).
	const nombreDe = (ref: any, id: any): string => {
		const fila: any = Object.values(ref || {}).find((r: any) => String(r.id) === String(id));
		return fila ? fila.name : "";
	};
	const [details, setDetails] = useState<EnrollmentRequestDetailsDTO | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [comment, setComment] = useState<string>("");
	const [prompt, setPrompt] = useState<PromptKind>(null);
	const [promptText, setPromptText] = useState<string>("");
	const [reviewerId, setReviewerId] = useState<number | "">("");
	const [promptFiles, setPromptFiles] = useState<File[]>([]);
	/* Corregir horario (rechazada por Caja): cuál renglón y a qué horario. */
	const [horarioDe, setHorarioDe] = useState<number | "">("");
	const [horarioA, setHorarioA] = useState<number | "">("");
	const [horarios, setHorarios] = useState<AvailableScheduleDTO[] | null>(null);
	const [filtroHorario, setFiltroHorario] = useState<string>("");
	const [commentFiles, setCommentFiles] = useState<File[]>([]);
	const [cuenta, setCuenta] = useState<number | null>(null);
	const temporizador = useRef<any>(null);

	const load = () => http.enrollmentRequestsService.details(campusId, requestId)
		.then((r) => setDetails(r.data))
		.catch((e) => setError(errorText(e)));

	useEffect(() => { load(); }, [campusId, requestId]);
	/*
		Identificación revisada con IA (19-oct-2026): lo que dice el documento contra lo capturado, en todos los caminos (formulario
		del cliente, Solicitud, en persona). Se pide aparte para que la matrícula abra sin esperar; la lectura queda guardada.
	*/
	const [idRev, setIdRev] = useState<RevisionIdentificacion | null>(null);
	const [idCargando, setIdCargando] = useState<boolean>(false);
	const idClave = details && details.isNewStudent && details.statusId !== REQUEST_STATUS.APROBADA && details.statusId !== REQUEST_STATUS.CANCELADA
		? [details.id, details.documents.map((x) => x.id + (x.rejected ? "r" : "")).join(","), details.studentData?.firstName, details.studentData?.lastName,
			details.studentData?.birthday, details.studentData?.identificationCode, JSON.stringify(details.studentData?.contacts || [])].join("|") : "";
	useEffect(() => {
		if (!idClave) { setIdRev(null); return; }
		let vivo = true;
		setIdCargando(true);
		http.enrollmentRequestsService.revisarIdentificacion(campusId, requestId)
			.then((r) => { if (vivo) setIdRev(r.data); })
			.catch(() => { if (vivo) setIdRev(null); })
			.finally(() => { if (vivo) setIdCargando(false); });
		return () => { vivo = false; };
	}, [idClave]);
	const usarIdentificacion = async () => {
		setBusy(true);
		try {
			const r = await http.enrollmentRequestsService.usarIdentificacion(campusId, requestId);
			setDetails(r.data);
			setNotice("Listo: nombre, fecha y número quedaron como en la identificación.");
		} catch (e) {
			setError(errorText(e));
		} finally {
			setBusy(false);
		}
	};
	//Mientras un contrato se está generando (tarda menos de un minuto) se vuelve a preguntar solo.
	const contratoEnCamino = (details?.contracts || []).some((c) => c.statusId === 0);
	useEffect(() => {
		if (!contratoEnCamino) {
			return;
		}
		const t = setInterval(() => { load(); }, 15000);
		return () => clearInterval(t);
	}, [contratoEnCamino, campusId, requestId]);
	const aprobando = useRef<boolean>(false);
	/*
		Menor de edad (Esteban, 2026-09-28): antes de matricular o de generar el contrato de nuevo se pregunta
		quién firma como tutor/a legal; ese contacto pasa a ser el primero, que es el que usa el contrato.
	*/
	const [tutorPara, setTutorPara] = useState<null | { accion: "approve" | "submit" | "regen"; contract?: RequestContractDTO }>(null);
	const [tutorIdx, setTutorIdx] = useState<number | null>(null);
	const tutorElegido = useRef<number | undefined>(undefined);

	/* El aviso al aprobar, con el género del alumno (0 Mujer, 1 Hombre, 2 Otro) y sin explicar de más. */
	const mensajeAprobada = (data: EnrollmentRequestDetailsDTO): string => {
		const g = data.studentGenderId;
		const forma = (f: string, m: string) => (g === 0 ? f : g === 1 ? m : m + "/a");
		const nombre = (data.studentName || "").split(" ")[0];
		return data.paymentIntentId
			? `Listo: ${nombre} quedó ${forma("enrolada", "enrolado")} · pendiente de pago en Caja.`
			: `Listo: ${nombre} quedó ${forma("inscrita", "inscrito")}.`;
	};
	useEffect(() => () => { if (temporizador.current) { clearInterval(temporizador.current); } }, []);

	const run = async (action: () => Promise<any>, successText: string | ((data: EnrollmentRequestDetailsDTO) => string)) => {
		setBusy(true);
		setError(null);
		setNotice(null);
		try {
			const r = await action();
			setDetails(r.data);
			setNotice(typeof successText === "function" ? successText(r.data) : successText);
			setPrompt(null);
			setPromptText("");
			setPromptFiles([]);
			onChanged();
		} catch (e) {
			setError(errorText(e));
		} finally {
			setBusy(false);
		}
	};

	/*
		Aprobar y matricular no se dispara de inmediato: deja 10 segundos para arrepentirse, que es
		cuando la gente se da cuenta del error (Esteban, 2026-09-22). No hay confirmación previa:
		la cuenta regresiva ES la confirmación.
	*/
	/*
		La aprobación NO se puede lanzar desde dentro de setCuenta: React puede ejecutar esa función
		dos veces, y aprobaba dos veces (la segunda fallaba con "Solo se aprueban solicitudes
		enviadas a revisión"). El contador solo cuenta; aprobar pasa en un efecto, una sola vez.
	*/
	const empezarAprobacion = () => {
		setError(null);
		setNotice(null);
		setCuenta(SEGUNDOS_PARA_DESHACER);
		temporizador.current = setInterval(() => {
			setCuenta((quedan) => (quedan === null ? null : quedan - 1));
		}, 1000);
	};
	useEffect(() => {
		if (cuenta !== 0) {
			return;
		}
		if (temporizador.current) {
			clearInterval(temporizador.current);
			temporizador.current = null;
		}
		setCuenta(null);
		if (aprobando.current) {
			return;
		}
		aprobando.current = true;
		run(() => http.enrollmentRequestsService.approve(campusId, requestId, tutorElegido.current), mensajeAprobada)
			.finally(() => { aprobando.current = false; });
	}, [cuenta]);
	const cancelarAprobacion = () => {
		if (temporizador.current) {
			clearInterval(temporizador.current);
			temporizador.current = null;
		}
		setCuenta(null);
	};

	const submit = () => {
		if (!window.confirm(S.confirmSubmit)) {
			return;
		}
		run(() => http.enrollmentRequestsService.submit(campusId, requestId, tutorElegido.current), S.submitted);
	};
	const necesitaTutor = () => !!(details && details.guardian && details.guardian.isMinor && details.guardian.options.length > 0);
	const seguirConTutor = (accion: "approve" | "submit" | "regen", contract?: RequestContractDTO) => {
		if (accion === "approve") {
			empezarAprobacion();
		} else if (accion === "submit") {
			submit();
		} else if (contract) {
			run(() => http.enrollmentRequestsService.regenerateContract(campusId, requestId, contract.id, tutorElegido.current), "Listo: el contrato se generó de nuevo.");
		}
	};
	const pedirTutor = (accion: "approve" | "submit" | "regen", contract?: RequestContractDTO) => {
		tutorElegido.current = undefined;
		if (!necesitaTutor()) {
			seguirConTutor(accion, contract);
			return;
		}
		setTutorIdx(null);
		setTutorPara({ accion, contract });
	};
	const confirmarTutor = () => {
		if (tutorPara === null || tutorIdx === null) return;
		const { accion, contract } = tutorPara;
		tutorElegido.current = tutorIdx;
		setTutorPara(null);
		seguirConTutor(accion, contract);
	};
	const openPrompt = (kind: PromptKind) => {
		setError(null);
		setPrompt(kind);
		setPromptText("");
		setPromptFiles([]);
		const opciones = details?.permissions.reassignOptions || [];
		setReviewerId(kind === "reassign" && opciones.length === 1 ? opciones[0].userId : "");
		if (kind === "horario") {
			const filas = details?.schedules || [];
			setHorarioDe(filas.length === 1 ? filas[0].scheduleId : "");
			setHorarioA("");
			setFiltroHorario("");
			// Sin alumno: la matrícula equivocada no cuenta como curso ya tomado (si no, los horarios por empezar no salen).
			setHorarios(null);
			http.scheduleService.getAvailableSchedules(campusId)
				.then((r) => setHorarios(r.data || []))
				.catch((e) => { setHorarios([]); setError(errorText(e)); });
		}
	};
	const confirmPrompt = () => {
		if (prompt === "return") {
			if (promptText.trim() === "") return;
			run(() => http.enrollmentRequestsService.returnToSales(campusId, requestId, promptText.trim(), promptFiles), S.returned);
		} else if (prompt === "reassign") {
			if (reviewerId === "") return;
			run(() => http.enrollmentRequestsService.reassign(campusId, requestId, Number(reviewerId), promptText.trim(), promptFiles), S.reassigned);
		} else if (prompt === "error") {
			if (promptText.trim().length < 10) return;
			run(() => http.enrollmentRequestsService.markError(campusId, requestId, promptText.trim()), S.markedError);
		} else if (prompt === "horario") {
			if (horarioDe === "" || horarioA === "") return;
			run(() => http.enrollmentRequestsService.changeSchedule(campusId, requestId, Number(horarioDe), Number(horarioA), promptText.trim(), promptFiles), S.scheduleChanged);
		} else if (prompt === "resend") {
			if (promptFiles.length < 1) return;
			run(() => http.enrollmentRequestsService.resendToCaja(campusId, requestId, promptText.trim(), promptFiles), S.resentToCaja);
		}
	};
	const addComment = () => {
		if (comment.trim() === "" && commentFiles.length < 1) return;
		run(() => http.enrollmentRequestsService.comment(campusId, requestId, comment.trim(), commentFiles), S.commentAdded)
			.then(() => { setComment(""); setCommentFiles([]); });
	};
	/* Pegar una captura en el cuadro de texto la adjunta. */
	const pegar = (e: React.ClipboardEvent, actuales: File[], poner: (f: File[]) => void) => {
		const capturas = capturasDelPortapapeles(e);
		if (capturas.length > 0) {
			e.preventDefault();
			poner([...actuales, ...capturas].slice(0, 5));
		}
	};
	// Sin await antes de abrirArchivo: la pestaña se abre en el mismo clic (en celular se bloqueaba).
	const viewContract = (c: RequestContractDTO, signed = false) =>
		abrirArchivo({ origen: "Contratos", nombre: (signed ? "Contrato firmado " : "Contrato ") + c.id + ".pdf", cargar: () => http.enrollmentRequestsService.downloadContract(campusId, requestId, c.id, signed).then((r) => r.data) })
			.catch((e) => setError(errorText(e)));
	const viewContractVersion = (c: RequestContractDTO, versionId: number) =>
		abrirArchivo({ origen: "Contratos", nombre: "Contrato " + c.id + " v" + versionId + ".pdf", cargar: () => http.enrollmentRequestsService.downloadContractVersion(campusId, requestId, c.id, versionId).then((r) => r.data) })
			.catch((e) => setError(errorText(e)));
	const viewDocument = (doc: RequestDocumentDTO) =>
		abrirArchivo({ origen: "Matrículas", nombre: doc.name || doc.type || "Documento", cargar: () => http.enrollmentRequestsService.downloadDocument(campusId, requestId, doc.id).then((r) => r.data) })
			.catch((e) => setError(errorText(e)));

	const d = details;
	const sd: any = d?.studentData || {};
	// Ficha que se enseña: la que confirmó la solicitud; si no trae (solicitud vieja de alumno registrado), la del perfil.
	const fi: any = sd.firstName ? sd : ((d?.existingStudent as any)?.studentData || sd);
	const p: any = d?.payment || {};
	//Rechazada por Caja: el alumno ya existe y está matriculado; los avisos de antes de aprobar ya no aplican.
	const open = d ? d.statusId !== REQUEST_STATUS.APROBADA && d.statusId !== REQUEST_STATUS.CANCELADA && !d.returnedByCaja : false;
	const tel = (valor: any) => telefonoBonito(valor, countryId) || "-";
	const nombreAlumno = d ? (d.existingStudent ? d.existingStudent.name : `${sd.firstName || ""} ${sd.lastName || ""}`.trim()) : "";
	const promptTitle = prompt === "horario" ? S.changeSchedulePrompt : prompt === "return" ? S.returnPrompt : prompt === "resend" ? S.resendPrompt : prompt === "error" ? S.markErrorPrompt : S.reassignPrompt;
	const opcionesReasignar = d?.permissions.reassignOptions || [];
	const ultimoRechazoCaja = d && d.returnedByCaja ? [...d.comments].filter((c) => c.typeId === 2).pop() || null : null;

	const secundarias: { key: string; label: string; icon?: any; onClick: () => void }[] = [];
	if (d && opcionesReasignar.length > 0) {
		secundarias.push({ key: "reassign", label: S.reassign, icon: solid("user-pen"), onClick: () => openPrompt("reassign") });
	}
	if (d && d.permissions.canMarkError) {
		secundarias.push({ key: "error", label: S.markError, icon: solid("ban"), onClick: () => openPrompt("error") });
	}
	if (d && d.permissions.canEdit) {
		secundarias.push({ key: "edit", label: S.edit, icon: solid("pen"), onClick: () => onEdit(requestId) });
	}
	const ancho = movil ? { flex: "1 1 0", padding: "10px 8px", fontSize: 15 } : undefined;
	const tam = movil ? "" : " btn-sm";
	const principales: JSX.Element[] = [];
	if (d && d.permissions.canSubmit) {
		principales.push(
			<button key="submit" type="button" className={"btn btn-primary" + tam} style={ancho} disabled={busy} onClick={() => (d.permissions.submitApproves ? pedirTutor("submit") : submit())}>
				<FontAwesomeIcon icon={solid("paper-plane")} />&nbsp;{d.modeId === REQUEST_MODE.PRESENCIAL && d.permissions.isRevisor ? S.submitPresencial : S.submit}
			</button>
		);
	}
	if (d && d.permissions.canChangeSchedule && (d.schedules || []).length > 0) {
		principales.push(
			<button key="horario" type="button" className={"btn btn-outline-primary" + tam} style={ancho} disabled={busy} onClick={() => openPrompt("horario")}>
				<FontAwesomeIcon icon={solid("calendar-days")} />&nbsp;{S.changeSchedule}
			</button>
		);
	}
	if (d && d.permissions.canResendToCaja) {
		principales.push(
			<button key="resend" type="button" className={"btn btn-primary" + tam} style={ancho} disabled={busy} onClick={() => openPrompt("resend")}>
				<FontAwesomeIcon icon={solid("paper-plane")} />&nbsp;{S.resendToCaja}
			</button>
		);
	}
	if (d && d.permissions.canReview) {
		principales.push(
			<button key="return" type="button" className={"btn btn-warning" + tam} style={ancho} disabled={busy} onClick={() => openPrompt("return")}>
				<FontAwesomeIcon icon={solid("xmark")} />&nbsp;{S.returnToSales}
			</button>
		);
		principales.push(
			<button key="approve" type="button" className={"btn btn-success" + tam} style={ancho} disabled={busy} onClick={() => pedirTutor("approve")}>
				<FontAwesomeIcon icon={solid("check")} />&nbsp;{movil ? S.approveShort : S.approve}
			</button>
		);
	}

	return (
		<React.Fragment>
		<Modal className="waModulo" show onHide={busy ? undefined : onClose} size="xl" centered scrollable fullscreen="sm-down">
			<Modal.Header closeButton={!busy}>
				<Modal.Title style={{ fontSize: movil ? 15 : 18, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
					{S.detailsTitle}
					{nombreAlumno ? <span style={{ fontWeight: 400 }}>{nombreAlumno}</span> : ""}
					{d ? <Badge bg={estadoVariant(d.statusId, d.status)} style={{ fontWeight: 500 }}>{d.status}</Badge> : ""}
					{d ? <Badge bg="secondary" style={{ fontWeight: 500 }}>{d.mode}</Badge> : ""}
					{d && d.returnedByCaja ? <Badge bg="oro" style={{ fontWeight: 500 }}>{S.returnedByCaja}</Badge> : ""}
					<span style={{ fontSize: 13, color: "var(--wa-apagado)", fontWeight: 400 }}>{S.requestNumber} {requestId}</span>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 15, paddingRight: movil ? 14 : 22, paddingLeft: movil ? 14 : 22 }}>
				{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : ""}
				{notice ? <div className="alert alert-success" style={{ fontSize: 15 }}>{notice}</div> : ""}
				{!d ? <div className="loader" /> : <>
					{d.returnedByCaja && ultimoRechazoCaja ? (
						<div className="waRechazo">
							<b>{S.returnedByCaja}</b> · {ultimoRechazoCaja.createdBy} · {formatLocalDateTime(ultimoRechazoCaja.createdAt)}
							<div style={{ whiteSpace: "pre-wrap" }}>{ultimoRechazoCaja.body}</div>
							<AdjuntosVista adjuntos={ultimoRechazoCaja.attachments}
								cargar={(i) => http.enrollmentRequestsService.commentAttachment(campusId, requestId, ultimoRechazoCaja.id, i)} />
						</div>
					) : ""}
					{open && d.validation.length > 0 ? (
						<div className="alert alert-warning" style={{ fontSize: 13, padding: "8px 12px" }}>
							<b>{S.validationTitle}</b>
							<ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>{d.validation.map((issue, i) => <li key={i}>{issue}</li>)}</ul>
						</div>
					) : ""}
					{open && d.isNewStudent && d.duplicates.length > 0 ? (
						<div className="alert alert-info" style={{ fontSize: 13, padding: "8px 12px" }}>
							<b>{S.duplicatesWarning}</b>
							<ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
								{d.duplicates.map((s) => <li key={"dup_" + s.id}>{s.name} · {s.identityCode || "-"} · {s.identificationCode || "-"} · {s.email || "-"} · {tel(s.phone1)}</li>)}
							</ul>
						</div>
					) : ""}

					<div className="row">
						<div className="col-lg-6">
							<h6>{S.summary}</h6>
							{/* Creada y enviada son casi el mismo instante: una sola línea (Esteban, 2026-09-22). */}
							<Row label={S.createdBy} movil={movil}>
								{d.createdByShort || d.createdBy || "-"} · {formatLocalDateTime(d.createdAt)}
								{d.submittedAt && formatLocalDateTime(d.submittedAt) !== formatLocalDateTime(d.createdAt)
									? <span style={{ color: "var(--wa-apagado)" }}> · {S.submittedAtShort} {formatLocalDateTime(d.submittedAt)}</span> : ""}
							</Row>
							<Row label={S.assignedTo} movil={movil}>{d.assignedToShort || d.assignedTo || S.unassigned}</Row>
							{d.reviewedBy ? <Row label={S.reviewedBy} movil={movil}>{d.reviewedByShort || d.reviewedBy} · {formatLocalDateTime(d.reviewedAt)}</Row> : ""}
							{d.cancelReason ? <Row label={d.statusId === REQUEST_STATUS.CANCELADA ? S.errorReason : "Motivo"} movil={movil}>{d.cancelReason}</Row> : ""}
							{d.enrollmentId ? <Row label={S.enrollmentCreated} movil={movil}>#{d.enrollmentId}</Row> : ""}
							{d.paymentIntent ? <Row label={S.paymentIntentCreated} movil={movil}>#{d.paymentIntent.id} · {d.paymentIntent.status}{d.paymentIntent.paymentId ? ` · pago #${d.paymentIntent.paymentId}` : ""}</Row> : ""}

							<h6 style={{ marginTop: 12 }}>{S.student}{d.isNewStudent ? <Badge bg="success" style={{ marginLeft: 8, fontWeight: 500 }}>Nuevo</Badge> : <Badge bg="info" text="dark" style={{ marginLeft: 8, fontWeight: 500 }}>Registrado</Badge>}</h6>
							{/*
								Siempre la ficha completa (Esteban, 2026-09-28: "le faltan dirección, ocupación, grado y fecha de
								nacimiento"). Con alumno ya registrado solo salían nombre, ID, teléfono y correo, aunque los datos
								estaban: ahora se ven los que confirmó la solicitud o, si no trae, los del perfil.
							*/}
							{idCargando && !idRev ? <div style={{ fontSize: 13, color: "var(--wa-apagado)", margin: "2px 0 6px" }}><FontAwesomeIcon icon={solid("id-card")} /> Revisando la identificación…</div> : null}
							{idRev && idRev.alumno ? (() => {
								const a = idRev.alumno;
								if (!a.leida) return <div className="waAviso" style={{ marginBottom: 8 }}><b>Identificación del alumno:</b> {a.aviso}</div>;
								if (a.diferencias.length > 0) return (
									<div className="waAviso" style={{ marginBottom: 8 }}>
										<b>No coincide con la identificación del alumno:</b>
										<ul style={{ margin: "4px 0 6px", paddingLeft: 18 }}>{a.diferencias.map((x) => <li key={x.campo}>{x.texto}</li>)}</ul>
										{a.editable ? <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={usarIdentificacion}>Usar los datos de la identificación</button> : null}
									</div>
								);
								return a.confianza === "baja"
									? <div className="waAviso" style={{ marginBottom: 8 }}>La foto de la identificación no se lee del todo bien: revisa nombre, fecha y número a mano.</div>
									: <div style={{ fontSize: 13, color: "var(--wa-azul-texto)", margin: "2px 0 6px" }}><FontAwesomeIcon icon={solid("id-card")} /> Nombre, fecha y número coinciden con la identificación.</div>;
							})() : null}
							{idRev && idRev.tutor ? (
								!idRev.tutor.leida ? <div className="waAviso" style={{ marginBottom: 8 }}><b>Identificación del tutor:</b> {idRev.tutor.aviso}</div>
									: idRev.tutor.diferencias.length > 0 ? <div className="waAviso" style={{ marginBottom: 8 }}>{idRev.tutor.diferencias[0].texto}</div> : null
							) : null}
							{d.existingStudent && d.existingStudent.identityCode ? <Row label="Matrícula" movil={movil}>{d.existingStudent.identityCode}</Row> : ""}
							<Row label="Nombre" movil={movil}>{fi.firstName} {fi.lastName}</Row>
							<Row label={S.fields.identificationCode} movil={movil}>{[nombreDe(basicInfo?.identificationTypeRef, fi.identificationTypeId), fi.identificationCode || "-"].filter(Boolean).join(" · ")}</Row>
							<Row label={S.fields.birthday} movil={movil}>{formatDate(fi.birthday)}</Row>
							<Row label="Teléfono" movil={movil}>{telefonosBonitos([fi.phone1, fi.phone2], countryId) || "-"}</Row>
							<Row label="Correo" movil={movil}>{fi.email || "-"}</Row>
							<Row label={S.fields.address} movil={movil}>{fi.address || "-"}{fi.zipcode ? ` · CP ${fi.zipcode}` : ""}</Row>
							{/* El último grado de estudios no salía en la revisión, solo en la forma (Esteban, 2026-09-24). */}
							<Row label={S.fields.academicDegreeId} movil={movil}>{nombreDe(basicInfo?.academicDegreeRef, fi.academicDegreeId) || "-"}{fi.lastSchoolName ? ` · ${fi.lastSchoolName}` : ""}</Row>
							<Row label={S.fields.currentOccupation} movil={movil}>{fi.currentOccupation || "-"}</Row>
							{Array.isArray(fi.contacts) && fi.contacts.length > 0 ? (
								<Row label={S.fields.contacts} movil={movil}>
									{fi.contacts.map((c: any, i: number) => (
										<div key={i}>{c.name} ({c.relation || "-"}) · {telefonoBonito(c.contactMedia, countryId) || c.contactMedia}</div>
									))}
								</Row>
							) : ""}
							{fi.observations ? <Row label={S.fields.observations} movil={movil}>{fi.observations}</Row> : ""}

						</div>
						<div className="col-lg-6">
							<h6 style={{ marginTop: movil ? 12 : 0 }}>{S.schedule}</h6>
							{(d.schedules || []).length < 1 ? <div style={{ color: "var(--wa-apagado)" }}>-</div> : (d.schedules || []).map((row) => (
								<div key={"sc_" + row.scheduleId} style={{ borderBottom: "1px dotted var(--wa-filo)", paddingBottom: 6, marginBottom: 6 }}>
									<Row label="Curso / Módulo" movil={movil}>{cursoCorto(row.courseName)} · {row.levelName}</Row>
									<Row label="Horario" movil={movil}>{horarioBonito(row.name, HORARIO_LISTA)}</Row>
									{/* Solo el mes y el año: las fechas exactas viven en el horario (Esteban, 2026-09-22). */}
									<Row label="Periodo" movil={movil}>{row.periodName || "-"}</Row>
									<Row label={S.subscription} movil={movil}>
										{Number(row.subscriptionPrice) === 0
											? <span style={{ color: "var(--wa-verde)", fontWeight: 700 }}>{S.free}</span>
											: formatMoney(row.subscriptionPrice)}
									</Row>
									<Row label={S.monthly} movil={movil}>{formatMoney(row.monthlyBillingPrice)}</Row>
									<Row label={S.notes} movil={movil}><span style={{ whiteSpace: "pre-wrap" }}>{row.notes || "-"}</span></Row>
								</div>
							))}

							<h6 style={{ marginTop: 12 }}>{S.advisors}</h6>
							{/* Sin asesoras = se eligió "Ninguno" a propósito (el formulario no deja avanzar sin decidir). */}
							<div>{d.advisors.length > 0 ? d.advisors.map((a) => a.name).join(", ") : (d.statusId === REQUEST_STATUS.BORRADOR ? "-" : "Ninguno")}</div>

							{/*
								Del pago solo viaja lo que Caja necesita leer: el comprobante y la nota.
								Monto, fecha y referencia los captura Caja al aplicarlo (Esteban, 2026-09-22).
							*/}
							<h6 style={{ marginTop: 12 }}>{S.payment}</h6>
							{d.modeId === REQUEST_MODE.PRESENCIAL ? (
								<Row label={S.paymentType} movil={movil}>{S.clientOnTheWay}</Row>
							) : (
								<Row label={S.paymentNotes} movil={movil}><span style={{ whiteSpace: "pre-wrap" }}>{p.notes || "-"}</span></Row>
							)}

							{(d.contracts || []).length > 0 ? (
								<>
									<h6 style={{ marginTop: 12 }}>{S.contract}</h6>
									{(d.contracts || []).map((c) => (
										<div key={"ct_" + c.id} style={{ padding: "3px 0", borderBottom: "1px dotted var(--wa-filo)" }}>
											<div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", columnGap: 14, rowGap: 2, alignItems: "center" }}>
												<span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
													{cursoCorto(c.course) || S.contract}
													<Badge bg={c.signed || c.statusId === 1 ? "success" : (c.statusId === 2 ? "oro" : "secondary")} style={{ marginLeft: 6, fontWeight: 500 }}>{c.signed ? S.contractSigned : c.status}</Badge>
												</span>
												<span style={{ display: "flex", flexWrap: "wrap", gap: "2px 14px", flexShrink: 0, marginLeft: "auto" }}>
													{c.signed ? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} onClick={() => viewContract(c, true)}>{S.contractViewSigned}</button> : ""}
													{c.statusId === 1 ? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} onClick={() => viewContract(c)}>{c.signed ? S.contractViewGenerated : S.contractView}</button> : ""}
													{c.canUploadSigned && c.statusId !== 0 ? (
														<label className="btn btn-link btn-sm" style={{ position: "relative", padding: 0, margin: 0, cursor: busy ? "default" : "pointer" }}>
															{c.signed ? S.contractReplaceSigned : S.contractUploadSigned}
															<input type="file" accept=".pdf,image/*" hidden disabled={busy}
																onChange={(e) => {
																	const f = e.target.files && e.target.files[0];
																	e.target.value = "";
																	if (f) { run(() => http.enrollmentRequestsService.uploadSignedContract(campusId, requestId, c.id, f), "Listo: el contrato firmado quedó en el perfil del alumno."); }
																}} />
														</label>
													) : ""}
													{!c.signed && !d.permissions.readOnly && (c.statusId === 1 || c.statusId === 2) ? (
														<button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} disabled={busy}
															onClick={() => pedirTutor("regen", c)}>{S.contractRegenerate}</button>
													) : ""}
												</span>
											</div>
											<FirmaEstado firma={c.firma} soloLectura={d.permissions.readOnly} onCambio={() => { load(); }} />
											<ContractHistory versions={c.versions} onOpen={(v) => viewContractVersion(c, v.id)} />
											{c.missingGuardian ? <div style={{ fontSize: 13, color: "var(--wa-oro)" }}>Menor de edad: faltan nombre y teléfono del tutor en contactos de emergencia.</div> : ""}
											{c.statusId === 2 || c.statusId === 3 ? <div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{c.error}</div> : ""}
										</div>
									))}
								</>
							) : ""}

							<h6 style={{ marginTop: 12 }}>{S.documents}</h6>
							{d.documents.length < 1 ? <div style={{ color: "var(--wa-apagado)" }}>-</div> : d.documents.map((doc) => (
								<div key={"doc_" + doc.id} style={{ padding: "3px 0", borderBottom: "1px dotted var(--wa-filo)" }}>
									<div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
										<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
											<span style={{ color: "var(--wa-apagado)", fontSize: 13 }}>{doc.type}</span>&nbsp;·&nbsp;
											<span style={doc.rejected ? { textDecoration: "line-through", color: "var(--wa-apagado)" } : undefined}>{doc.name}</span>
											{doc.rejected ? <Badge bg="oro" style={{ marginLeft: 6, fontWeight: 500 }}>{S.docRejected}</Badge> : ""}
										</span>
										<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, whiteSpace: "nowrap" }} onClick={() => viewDocument(doc)}>{S.download}</button>
									</div>
									{/* Historia del comprobante: por qué lo rechazó Caja y con qué comentario llegó el nuevo. */}
									{doc.rejected ? (
										<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{doc.rejectedBy} · {formatLocalDateTime(doc.rejectedAt)}: {doc.rejectionReason}</div>
									) : ""}
									{doc.comment ? <div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{doc.createdBy} · {formatLocalDateTime(doc.createdAt)}: {doc.comment}</div> : ""}
								</div>
							))}
						</div>
					</div>

					<h6 style={{ marginTop: 14 }}>{S.comments}</h6>
					<div style={{ maxHeight: 220, overflow: "auto", border: "1px solid var(--wa-filo)", borderRadius: 6, padding: "6px 10px" }}>
						{d.comments.length < 1 ? <div style={{ color: "var(--wa-apagado)" }}>-</div> : [...d.comments].reverse().map((c) => (
							<div key={"cm_" + c.id} style={{ padding: "4px 0", borderBottom: "1px dotted var(--wa-filo)", color: c.typeId === 3 ? "var(--wa-apagado)" : undefined, fontStyle: c.typeId === 3 ? "italic" : undefined }}>
								{c.typeId === 2 ? <Badge bg="oro" style={{ marginRight: 6, fontWeight: 500 }}>{S.whatToFix}</Badge> : ""}
								<span style={{ whiteSpace: "pre-wrap" }}>{c.body}</span>
								<AdjuntosVista adjuntos={c.attachments} cargar={(i) => http.enrollmentRequestsService.commentAttachment(campusId, requestId, c.id, i)} />
								<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{c.createdBy} · {formatLocalDateTime(c.createdAt)}</div>
							</div>
						))}
					</div>
					{/* Supervisora que abre la finalizada de otra: solo consulta (Esteban, 2026-09-27). */}
					{d.permissions.readOnly ? "" : <>
					<div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "flex-start" }}>
						<textarea className="form-control form-control-sm" rows={1} placeholder={S.commentPlaceholder} value={comment} disabled={busy}
							onChange={(e) => setComment(e.target.value)} onPaste={(e) => pegar(e, commentFiles, setCommentFiles)}
							onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }} />
						<button type="button" className="btn btn-outline-secondary btn-sm" style={{ whiteSpace: "nowrap" }} disabled={busy || (comment.trim() === "" && commentFiles.length < 1)} onClick={addComment}>{S.addComment}</button>
					</div>
					<div style={{ marginTop: 6 }}><AdjuntosInput files={commentFiles} onChange={setCommentFiles} onError={setError} disabled={busy} /></div>
					</>}
					{d.enrollmentId ? <div style={{ fontSize: 13, color: "var(--wa-apagado)", marginTop: 4 }}>{S.commentsGoToProfile}</div> : ""}
				</>}
			</Modal.Body>
			{/* Sin acciones no hay pie: cerrar ya está en la X (Esteban, 2026-09-23). */}
			{cuenta === null && secundarias.length < 1 && principales.length < 1 ? "" : (
			<Modal.Footer style={{ flexWrap: "wrap", gap: 6, justifyContent: movil ? "stretch" : "flex-end" }}>
				{cuenta !== null ? (
					<div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap",
						background: "var(--wa-oro-suave)", border: "1px solid var(--wa-oro)", borderRadius: 6, padding: "8px 12px" }}>
						<span><b>{S.approvingIn(cuenta)}</b> {S.approvingHint}</span>
						<button type="button" className="btn btn-outline-dark btn-sm" onClick={cancelarAprobacion}>{S.undo}</button>
					</div>
				) : (
					movil ? (
						/*
							En el teléfono siete botones apilados se comían media pantalla. Las dos decisiones
							van lado a lado; lo que se usa poco, en "Más acciones"; cerrar ya está en la X.
							(Esteban, 2026-09-23)
						*/
						<div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
							{secundarias.length > 0 ? (
								<Dropdown className="w-100" drop="up">
									<Dropdown.Toggle className="btn btn-outline-secondary w-100" variant="" disabled={busy}>{S.moreActions}</Dropdown.Toggle>
									<Dropdown.Menu className="w-100">
										{secundarias.map((a) => (
											<Dropdown.Item key={a.key} as="button" type="button" style={{ padding: "10px 16px", fontSize: 15 }} onClick={a.onClick}>
												{a.icon ? <><FontAwesomeIcon icon={a.icon} />&nbsp;</> : ""}{a.label}
											</Dropdown.Item>
										))}
									</Dropdown.Menu>
								</Dropdown>
							) : ""}
							{principales.length > 0 ? <div style={{ display: "flex", gap: 8 }}>{principales}</div> : ""}
						</div>
					) : (
					<>
						{secundarias.map((a) => (
							<button key={a.key} type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={a.onClick}>
								{a.icon ? <><FontAwesomeIcon icon={a.icon} />&nbsp;</> : ""}{a.label}
							</button>
						))}
						{principales}
					</>
					)
				)}
			</Modal.Footer>
			)}
		</Modal>

		{/*
			La razón se pide en su propia ventana: antes convivía con el cuadro de comentarios y no se
			sabía dónde escribir (Esteban, 2026-09-22).
		*/}
		<Modal className="waModulo waModalArriba" backdropClassName="waFondoArriba" show={prompt !== null} onHide={busy ? undefined : () => setPrompt(null)} centered size={prompt === "horario" ? "lg" : undefined} scrollable={prompt === "horario"} fullscreen={prompt === "horario" ? "sm-down" : undefined}>
			<Modal.Header closeButton={!busy}><Modal.Title style={{ fontSize: 16 }}>{promptTitle}</Modal.Title></Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 15 }}>
				{/* El error también aquí: el de la ficha queda tapado por esta ventana. */}
				{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : ""}
				{prompt === "reassign" ? (
					<>
						<label style={{ position: "static", fontSize: 14, color: "var(--wa-apagado)", display: "block", marginBottom: 3 }}>{S.reassignTo}</label>
						<select className="form-select" value={reviewerId} disabled={busy} onChange={(e) => setReviewerId(e.target.value === "" ? "" : Number(e.target.value))}>
							<option value="">{S.select}</option>
							{opcionesReasignar.map((o) => <option key={"rv_" + o.userId} value={o.userId}>{o.name} · {o.hint}</option>)}
						</select>
					</>
				) : ""}
				{prompt === "resend" ? (
					<div style={{ marginBottom: 10 }}>
						<label style={{ position: "static", fontSize: 14, color: "var(--wa-apagado)", display: "block", marginBottom: 3 }}>{S.newReceipt} *</label>
						<AdjuntosInput files={promptFiles} onChange={setPromptFiles} onError={setError} disabled={busy} etiqueta={S.attachReceipt} />
					</div>
				) : ""}
				{prompt === "error" ? <div style={{ fontSize: 14, color: "var(--wa-apagado)" }}>{S.markErrorHint}</div> : ""}
				{prompt === "horario" ? (
					<div style={{ marginBottom: 6 }}>
						<div style={{ fontSize: 14, color: "var(--wa-apagado)", marginBottom: 10 }}>{S.changeScheduleHint}</div>
						{(d?.schedules || []).length > 1 ? (
							<>
								<label style={{ position: "static", fontSize: 14, color: "var(--wa-apagado)", display: "block", marginBottom: 3 }}>{S.changeScheduleWhich} *</label>
								<select className="form-select" style={{ marginBottom: 10 }} value={horarioDe} disabled={busy} onChange={(e) => setHorarioDe(e.target.value === "" ? "" : Number(e.target.value))}>
									<option value="">{S.select}</option>
									{(d?.schedules || []).map((row) => <option key={"hd_" + row.scheduleId} value={row.scheduleId}>{cursoCorto(row.courseName)} · {horarioBonito(row.name, HORARIO_LISTA)} · {row.periodName}</option>)}
								</select>
							</>
						) : (d?.schedules || []).map((row) => (
							<div key={"hd_" + row.scheduleId} style={{ fontSize: 14, marginBottom: 10 }}>
								<span style={{ color: "var(--wa-apagado)" }}>Ahora:</span> {cursoCorto(row.courseName)} · {horarioBonito(row.name, HORARIO_LISTA)} · {row.periodName}{row.code ? " · " + row.code : ""}
							</div>
						))}
						<SchedulePicker schedules={horarios} picked={horarioA === "" ? [] : [horarioA]} tarjetas
							onToggle={(h) => setHorarioA(horarioA === h.id ? "" : h.id)} filter={filtroHorario} onFilter={setFiltroHorario}
							disabled={busy} title={S.changeScheduleNew + " *"} />
						<label style={{ position: "static", fontSize: 14, color: "var(--wa-apagado)", display: "block", margin: "10px 0 3px" }}>{S.changeScheduleReceipt}</label>
						<AdjuntosInput files={promptFiles} onChange={setPromptFiles} onError={setError} disabled={busy} etiqueta={S.attachReceipt} />
					</div>
				) : ""}
				<label style={{ position: "static", fontSize: 14, color: "var(--wa-apagado)", display: "block", margin: "10px 0 3px" }}>
					{prompt === "return" ? S.whatToFix + " *" : prompt === "resend" || prompt === "horario" ? S.commentForCaja : prompt === "error" ? S.markErrorReason + " *" : S.commentOptional}
				</label>
				<textarea className="form-control" rows={prompt === "horario" ? 2 : 3} autoFocus={prompt !== "reassign" && prompt !== "horario"} value={promptText} disabled={busy}
					placeholder={prompt === "return" ? S.returnPlaceholder : prompt === "resend" || prompt === "horario" ? S.resendPlaceholder : prompt === "error" ? S.markErrorPlaceholder : S.reassignPlaceholder}
					onChange={(e) => setPromptText(e.target.value)} onPaste={(e) => pegar(e, promptFiles, setPromptFiles)} />
				{prompt !== "resend" && prompt !== "error" && prompt !== "horario" ? <div style={{ marginTop: 8 }}><AdjuntosInput files={promptFiles} onChange={setPromptFiles} onError={setError} disabled={busy} /></div> : ""}
			</Modal.Body>
			<Modal.Footer>
				<button type="button" className="btn btn-primary"
					disabled={busy || (prompt === "return" && promptText.trim() === "") || (prompt === "reassign" && reviewerId === "") || (prompt === "resend" && promptFiles.length < 1)
						|| (prompt === "error" && promptText.trim().length < 10) || (prompt === "horario" && (horarioDe === "" || horarioA === ""))}
					onClick={confirmPrompt}>{prompt === "horario" ? S.changeScheduleSend : prompt === "resend" ? S.resendToCaja : prompt === "return" ? S.returnToSales : prompt === "error" ? S.markError : S.reassign}</button>
			</Modal.Footer>
		</Modal>

		<TutorContratoModal show={tutorPara !== null} opciones={d?.guardian?.options || []} elegido={tutorIdx} countryId={countryId} movil={movil}
			onElegir={setTutorIdx} onCerrar={() => setTutorPara(null)} onContinuar={confirmarTutor} />
		</React.Fragment>
	);
};

export default RequestDetailsModal;
